/**
 * PulseDetailLayout — the shared chrome for every TurfPulse detail page.
 *
 * Composes the "Top Tier" detail screen reference (2026-05-22):
 *   ← header (with title + info icon)
 *   ICON + LABEL
 *   BIG NUMBER + status badge
 *   sparkline graph (48h history) — passed in via `children`
 *   ideal range / target tiles
 *   insight card
 *   recommendation card
 *   bottom nav (Home / Trends / Alerts / Settings)
 *
 * Each detail page only has to compose its tiles + sparkline + copy. All
 * other layout decisions (spacing, dark theme, bottom nav) live here so
 * the 5 screens stay visually identical.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Info, Home, Clock, BarChart3, Bell, Settings as SettingsIcon } from 'lucide-react';
import { useUnreadAlerts } from '../../hooks/useUnreadAlerts';
import { AlertDot } from './AlertDot';

export function PulseDetailLayout({
  title,
  icon: Icon,
  iconColor = 'text-emerald-400',
  badgeText,
  badgeColor = 'text-amber-400',
  bigValue,
  bigValueColor = 'text-emerald-400',
  bigValueUnit = '',
  children,
}) {
  const navigate = useNavigate();
  const { total: unreadTotal, high: unreadHigh } = useUnreadAlerts();
  return (
    <div className="min-h-screen bg-black text-slate-100 pb-24">
      <div className="max-w-md mx-auto px-4 pt-3">
        {/* Header */}
        <div className="flex items-center justify-between py-2">
          <button
            data-testid="pulse-detail-back"
            onClick={() => navigate('/pulse')}
            className="p-2 -ml-2 rounded-full hover:bg-slate-800 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-slate-200" />
          </button>
          <div className="text-base font-semibold tracking-tight">{title}</div>
          <button className="p-2 -mr-2 rounded-full hover:bg-slate-800 transition-colors">
            <Info className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Icon + label row */}
        <div className="flex items-center gap-2 mt-3">
          {Icon && <Icon className={`w-5 h-5 ${iconColor}`} />}
          <span className="text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold">
            {title}
          </span>
        </div>

        {/* Big number (hide block entirely if no value — eg Windows page) */}
        {bigValue != null && (
          <div
            data-testid="pulse-detail-big-value"
            className={`text-6xl font-extrabold leading-none mt-2 ${bigValueColor}`}
          >
            {bigValue}
            {bigValueUnit && (
              <span className="text-2xl font-bold align-baseline ml-1 text-slate-300">
                {bigValueUnit}
              </span>
            )}
          </div>
        )}
        {badgeText && (
          <div className="flex items-center gap-2 mt-2">
            <span className={`w-2 h-2 rounded-full ${badgeColor.replace('text-', 'bg-')}`} />
            <span className={`text-sm font-semibold ${badgeColor}`}>{badgeText}</span>
          </div>
        )}

        <div className="mt-4 space-y-3">{children}</div>
      </div>

      {/* Bottom nav — same shift as dashboard so the emergent watermark
          doesn't sit on top of Alerts/Settings on hosted preview. Five
          icons mirrors the home dashboard so users don't lose History
          when they drill into a detail card. */}
      <div className="fixed bottom-0 inset-x-0 bg-slate-950/95 backdrop-blur border-t border-slate-800 z-30">
        <div className="max-w-[calc(100%-185px)] px-3 py-1.5 grid grid-cols-5 text-center text-[10px] text-slate-400 gap-1">
          <button data-testid="nav-home"     onClick={() => navigate('/pulse')}    className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><Home className="w-5 h-5"/><span>Home</span></button>
          <button data-testid="nav-history"  onClick={() => navigate('/activity')} className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><Clock className="w-5 h-5"/><span>History</span></button>
          <button data-testid="nav-trends"   onClick={() => navigate('/trends')}   className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><BarChart3 className="w-5 h-5"/><span>Trends</span></button>
          <button data-testid="nav-alerts"   onClick={() => navigate('/alerts')}   className="relative flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><span className="relative"><Bell className="w-5 h-5"/><AlertDot total={unreadTotal} high={unreadHigh} /></span><span>Alerts</span></button>
          <button data-testid="nav-settings" onClick={() => navigate('/profile')}  className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><SettingsIcon className="w-5 h-5"/><span>Settings</span></button>
        </div>
      </div>
    </div>
  );
}

// ─── Pluggable card primitives used across all 5 detail pages ───────────────
export function PulseTile({ label, value, valueColor = 'text-slate-100', subline, testid }) {
  return (
    <div data-testid={testid}
         className="bg-slate-900/80 border border-slate-800 rounded-2xl px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-bold ${valueColor} mt-0.5`}>{value ?? '—'}</div>
      {subline && <div className="text-[11px] text-slate-400 mt-0.5">{subline}</div>}
    </div>
  );
}

export function PulseInsight({ tone = 'emerald', icon: Icon, label = 'Insight', children, testid }) {
  const tones = {
    emerald: { border: 'border-emerald-900/60', text: 'text-emerald-400' },
    amber:   { border: 'border-amber-900/60',   text: 'text-amber-400' },
    rose:    { border: 'border-rose-900/60',    text: 'text-rose-400' },
    purple:  { border: 'border-purple-900/60',  text: 'text-purple-400' },
    sky:     { border: 'border-sky-900/60',     text: 'text-sky-400' },
  };
  const t = tones[tone] || tones.emerald;
  return (
    <div data-testid={testid}
         className={`bg-slate-900/80 border ${t.border} rounded-2xl px-4 py-3 flex gap-3`}>
      {Icon && <Icon className={`w-5 h-5 ${t.text} flex-shrink-0 mt-0.5`} />}
      <div>
        <div className={`text-[11px] uppercase tracking-wider font-semibold ${t.text}`}>{label}</div>
        <div className="text-sm text-slate-200 leading-snug mt-0.5">{children}</div>
      </div>
    </div>
  );
}
